/**
 * Flutter WebView와의 통신을 위한 브리지 유틸리티
 */

import { devLog, flutterBridgeLog, logoutLog } from "./devLogger";

interface FlutterMessage {
  type: string;
  data: any;
}

interface TokenData {
  accessToken: string;
  refreshToken?: string;
  userInfo: any;
  timestamp: number;
}

export class FlutterBridge {
  private static instance: FlutterBridge;
  private receivedRefreshToken: string | null = null;
  private refreshTokenTimestamp: number | null = null;

  static getInstance(): FlutterBridge {
    if (!FlutterBridge.instance) {
      FlutterBridge.instance = new FlutterBridge();
    }
    return FlutterBridge.instance;
  }

  /**
   * Flutter 앱인지 확인
   */
  isFlutterWebView(): boolean {
    devLog('Flutter 환경 체크 시작');
    devLog('User Agent:', navigator.userAgent);

    // JavaScript에서 설정된 Flutter 플래그 확인
    const hasFlutterFlag = (window as any).isFlutterApp === true;
    
    // User Agent에서 Flutter WebView 확인
    const userAgent = navigator.userAgent;
    const isFlutterUA = userAgent.includes('Flutter') || userAgent.includes('FlutterWebView');
    // Android WebView 식별자(wv)가 있는 경우 Flutter 앱 내 WebView일 가능성이 높음
    const isAndroidWebViewUA = /Android/i.test(userAgent) && /; wv\)|\bwv\b/i.test(userAgent);
    
    // JavaScript 채널 확인
    const hasFlutterBridge = typeof (window as any).FlutterBridge !== 'undefined';
    const hasFlutterObject = typeof (window as any).flutter !== 'undefined';
    const hasWebkit = (window as any).webkit?.messageHandlers?.flutter !== undefined;
    const hasInAppWebView = (window as any).flutter_inappwebview !== undefined;
    
    // 종합 판단
    const isFlutter =
      hasFlutterFlag ||
      hasFlutterBridge ||
      hasFlutterObject ||
      isFlutterUA ||
      isAndroidWebViewUA ||
      hasWebkit ||
      hasInAppWebView;

    devLog('Flutter 환경 체크 결과:', {
      hasFlutterFlag,
      isFlutterUA,
      isAndroidWebViewUA,
      hasFlutterBridge,
      hasFlutterObject,
      hasWebkit,
      hasInAppWebView,
      finalResult: isFlutter
    });
    
    return isFlutter;
  }

  /**
   * Flutter로 메시지 전송
   */
  sendMessage(message: FlutterMessage): void {
    flutterBridgeLog('Flutter로 메시지 전송 시도', message);

    try {
      // 1. Flutter WebView의 JavaScript 채널 확인
      if ((window as any).FlutterBridge && (window as any).FlutterBridge.postMessage) {
        flutterBridgeLog('FlutterBridge.postMessage 사용');
        (window as any).FlutterBridge.postMessage(JSON.stringify(message));
        return;
      }
      
      // 2. PostMessage로 전송 (가장 일반적인 방법)
      if (window.parent && window.parent !== window) {
        flutterBridgeLog('window.parent.postMessage 사용');
        window.parent.postMessage(message, '*');
        return;
      }
      
      // 3. 일반 window postMessage 시도
      flutterBridgeLog('window.postMessage 사용');
      window.postMessage(message, '*');
      
      // 4. 콘솔에 메시지 출력 (디버깅용)
      flutterBridgeLog('전송된 메시지', JSON.stringify(message, null, 2));

      // 5. localStorage에 백업 저장
      try {
        localStorage.setItem('flutter_last_message', JSON.stringify({
          message,
          timestamp: Date.now(),
          sent: true
        }));
        flutterBridgeLog('localStorage에 메시지 백업 저장 완료');
      } catch (storageError) {
        flutterBridgeLog('localStorage 저장 실패', storageError);
      }
      
    } catch (error) {
      flutterBridgeLog('Flutter로 메시지 전송 실패', error);

      // 에러 발생 시에도 localStorage에 기록
      try {
        localStorage.setItem('flutter_last_error', JSON.stringify({
          message,
          error: error instanceof Error ? error.message : String(error),
          timestamp: Date.now()
        }));
      } catch (storageError) {
        flutterBridgeLog('에러 로그 저장 실패', storageError);
      }
    }
  }

  /**
   * 로그인 토큰을 Flutter로 전송하고 디바이스 토큰 등록 요청
   */
  sendLoginToken(tokenData: TokenData): void {
    const message: FlutterMessage = {
      type: 'LOGIN_SUCCESS',
      data: {
        ...tokenData,
        timestamp: Date.now()
      }
    };

    devLog('Flutter로 로그인 성공 알림 전송:', message);
    this.sendMessage(message);
    
    // localStorage에도 저장 (백업용)
    try {
      localStorage.setItem('flutter_token_data', JSON.stringify(message.data));
    } catch (error) {
      devLog('토큰 데이터 localStorage 저장 실패:', error);
    }
  }

  /**
   * 로그아웃을 Flutter로 알림
   */
  sendLogout(): void {
    const message: FlutterMessage = {
      type: 'LOGOUT',
      data: {
        timestamp: Date.now()
      }
    };

    logoutLog('Flutter로 로그아웃 알림 전송', message);
    this.sendMessage(message);
    
    // Flutter 브릿지 내부 상태 초기화
    this.clearInternalState();
    
    // localStorage 정리
    try {
      localStorage.removeItem('flutter_token_data');
      localStorage.removeItem('flutter_login_sent');
      logoutLog('Flutter 관련 localStorage 데이터 정리 완료');
    } catch (error) {
      logoutLog('Flutter localStorage 정리 실패', error);
    }
  }

  /**
   * Flutter 브릿지 내부 상태 초기화
   */
  private clearInternalState(): void {
    logoutLog('Flutter 브릿지 내부 상태 초기화 시작');
    
    // 수신된 refresh token 정보 초기화
    this.receivedRefreshToken = null;
    this.refreshTokenTimestamp = null;
    
    // sessionStorage 디버그 데이터 정리 (개발 모드)
    if (process.env.NODE_ENV === 'development') {
      try {
        sessionStorage.removeItem('flutter_refresh_token_debug');
        logoutLog('Flutter 디버그 데이터 정리 완료');
      } catch (error) {
        logoutLog('Flutter 디버그 데이터 정리 실패', error);
      }
    }
    
    logoutLog('Flutter 브릿지 내부 상태 초기화 완료');
  }

  /**
   * 사용자 정보 업데이트를 Flutter로 전송
   */
  sendUserUpdate(userInfo: any): void {
    this.sendMessage({
      type: 'REACT_USER_UPDATE',
      data: userInfo
    });
  }

  /**
   * Flutter 네이티브 카카오 로그인 요청
   */
  requestKakaoNativeLogin(): void {
    this.sendMessage({
      type: 'KAKAO_LOGIN_REQUEST',
      data: {
        timestamp: Date.now()
      }
    });
  }

  /**
   * Flutter 네이티브 구글 로그인 요청
   */
  requestGoogleNativeLogin(): void {
    this.sendMessage({
      type: 'GOOGLE_LOGIN_REQUEST',
      data: {
        timestamp: Date.now()
      }
    });
  }

  /**
   * FCM 수신 상태 처리 (Flutter로부터 수신)
   */
  handleFCMNotificationReceived(): void {
    devLog('🔔 Flutter로부터 FCM 알림 수신 상태 처리');
    
    // FCM 수신 이벤트를 React 앱 전체에 전파
    window.dispatchEvent(new CustomEvent('flutter-fcm-received', {
      detail: {
        hasNotification: true,
        timestamp: Date.now()
      }
    }));
  }

  /**
   * Flutter로부터 리프레시 토큰 수신 처리
   */
  handleRefreshTokenFromFlutter(refreshToken: string, accessToken?: string, userInfo?: any): void {
    this.receivedRefreshToken = refreshToken;
    this.refreshTokenTimestamp = Date.now();
    
    devLog('🔄 Flutter로부터 리프레시 토큰 수신:', {
      refreshTokenLength: refreshToken?.length || 0,
      refreshTokenStart: refreshToken ? refreshToken.substring(0, 30) + '...' : '없음',
      accessTokenLength: accessToken?.length || 0,
      accessTokenStart: accessToken ? accessToken.substring(0, 30) + '...' : '없음',
      userInfo: userInfo?.name || userInfo?.email || '정보 없음',
      timestamp: new Date().toISOString()
    });

    // 토큰 값 검증
    if (!refreshToken || refreshToken.length < 10) {
      devLog('⚠️ 수신된 리프레시 토큰이 유효하지 않음:', {
        token: refreshToken,
        length: refreshToken?.length || 0
      });
      return;
    }

    // 개발 모드에서 sessionStorage에 임시 저장 (디버그용)
    if (process.env.NODE_ENV === 'development') {
      try {
        sessionStorage.setItem('flutter_refresh_token_debug', JSON.stringify({
          refreshToken,
          accessToken,
          userInfo,
          receivedAt: new Date().toISOString(),
          tokenLength: refreshToken.length,
          tokenPreview: refreshToken.substring(0, 50) + '...'
        }));
        devLog('🔍 디버그용 리프레시 토큰 sessionStorage 저장 완료');
      } catch (error) {
        devLog('디버그용 토큰 저장 실패:', error);
      }
    }
  }

  /**
   * 현재 수신한 리프레시 토큰 반환
   */
  getReceivedRefreshToken(): string | null {
    return this.receivedRefreshToken;
  }

  /**
   * 현재 저장된 리프레시 토큰 정보 반환 (디버그용)
   */
  getDebugTokenInfo(): { refreshToken: string | null; timestamp: number | null; sessionData: any } {
    let sessionData = null;
    
    if (process.env.NODE_ENV === 'development') {
      try {
        const saved = sessionStorage.getItem('flutter_refresh_token_debug');
        if (saved) {
          sessionData = JSON.parse(saved);
        }
      } catch (error) {
        devLog('디버그 토큰 정보 불러오기 실패:', error);
      }
    }

    return {
      refreshToken: this.receivedRefreshToken,
      timestamp: this.refreshTokenTimestamp,
      sessionData
    };
  }

  /**
   * Flutter 메시지 리스너 등록 (리프레시 토큰 포함)
   */
  setupRefreshTokenListener(): () => void {
    const messageListener = (event: MessageEvent) => {
      try {
        const message = event.data;
        if (message && message.type === 'FLUTTER_INIT_WITH_REFRESH_TOKEN') {
          devLog('📱 Flutter 초기화 메시지 수신:', message);

          const { refreshToken, accessToken, userInfo } = message.data;
          if (refreshToken) {
            this.handleRefreshTokenFromFlutter(refreshToken, accessToken, userInfo);
            
            // UserAuthContext로 자동 로그인 시도 이벤트 발생
            window.dispatchEvent(new CustomEvent('flutter-auto-login', {
              detail: { refreshToken, accessToken, userInfo }
            }));

            devLog('🔄 Flutter 리프레시 토큰 자동 로그인 이벤트 발생');
          }
        }
        
        // FCM 수신 메시지 처리
        if (message && message.type === 'FCM_NOTIFICATION_RECEIVED') {
          devLog('🔔 Flutter FCM 알림 수신 메시지 처리:', message);
          this.handleFCMNotificationReceived();
        }
      } catch (error) {
        devLog('Flutter 메시지 처리 실패:', error);
      }
    };

    // 로그아웃 이벤트 리스너
    const logoutListener = (event: Event) => {
      const customEvent = event as CustomEvent;
      devLog('🔓 사용자 로그아웃 이벤트 수신:', customEvent.detail);
      
      // Flutter 브릿지 내부 상태 초기화
      this.clearInternalState();
    };

    window.addEventListener('message', messageListener);
    window.addEventListener('user-logout', logoutListener);
    
    return () => {
      window.removeEventListener('message', messageListener);
      window.removeEventListener('user-logout', logoutListener);
    };
  }
}

// 싱글톤 인스턴스 내보내기
export const flutterBridge = FlutterBridge.getInstance();
